<script>
    import {
        NotificationModal,
        PageLoading,
        RichTextEditor,
        SvelteTable,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        NoteTypes,
        addIcon,
        deleteIcon,
        editIcon,
    } from "../../Utils/utilConstants.svelte";

    import {
        getFormatDateAndTime,
        getText,
        isObjectEmpty,
        showError,
        showInfo,
    } from "../../Utils/utilFunctions.svelte";

    export let isModalOpen = false,
        encounter = {},
        closeModal = () => {},
        afterModifyNotes = () => {},
        includePatientNotes = false;

    $: modalTitle = `All notes ${isObjectEmpty(encounter) ? "" : `of encounter ${encounter.id}`}`;

    $: if (
        isModalOpen &&
        (encounter.hasNotes ||
            (includePatientNotes ? encounter?.patient?.hasNotes : true))
    )
        loadNotes();

    let isLoadingNotes = false,
        allNotes = [];

    let selectedNote = {},
        isUpdatingNote = false,
        isCreateNoteModalOpen = false,
        isEditNoteModalOpen = false,
        isDeleteNoteModalOpen = false,
        hasSelectedNotesChanged = false;

    let createNoteContentInputRef, editNoteContentInputRef;

    const headerOptions = [
        {
            optionText: "Add Note",
            iFont: addIcon,
            iconName: "addIcon",
            function: () => _openCreateNoteModal(),
        },
    ];

    const columns = [
        {
            key: "content",
            title: "Note Content",
            headerClass: "text-left pl-3 w-1/2",
            class: "px-3 text-left cursor-default",
            renderValue: (v) => getText(v.content),
        },
        {
            key: "author",
            title: "Added By",
            headerClass: "text-left pl-3 w-1/4",
            class: "px-3 text-left cursor-default",
            renderValue: (v) => getText(v.userName),
        },
        {
            key: "date",
            title: "Date",
            headerClass: "text-left pl-3 w-1/4",
            class: "px-3 text-left cursor-default",
            renderValue: (v) => getFormatDateAndTime(v.createdDate),
        },
        {
            key: "icons",
            title: "Actions",
            headerClass: "text-left pl-3 w-1/4",
            class: "px-3 text-left cursor-default",
            renderValue: (v) => _renderActionIcons(v),
        },
    ];

    const loadNotes = async (includeAfterModifyNotes = false) => {
        isLoadingNotes = true;

        await apiMethods.getMethod
            .getNotes(NoteTypes.PatientEncounter, encounter.id)
            .then((res) => {
                if (!res || res.error) {
                    showError(
                        res.error ??
                            "Failed to load encounter notes, please refresh the page and try again and contact support if the issue persists.",
                    );
                    closeModal();
                } else allNotes = [...res.results];
            })
            .catch((err) => {
                showError(err);
                closeModal();
            });

        if (includePatientNotes && encounter?.patient?.hasNotes)
            await apiMethods.getMethod
                .getNotes(NoteTypes.Patient, encounter.patient.id)
                .then((res) => {
                    if (!res || res.error) {
                        showError(
                            res.error ??
                                "Failed to load patient notes, please refresh the page and try again and contact support if the issue persists.",
                        );
                        closeModal();
                    } else allNotes = [...allNotes, ...res.results];
                })
                .catch((err) => {
                    showError(err);
                    closeModal();
                });

        includeAfterModifyNotes && (await afterModifyNotes());

        isLoadingNotes = false;
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "icons") {
            if (className.includes("editIcon")) _openEditNoteModal(rowData);
            else if (className.includes("deleteIcon"))
                _openDeleteNoteModal(rowData);
        }
    };

    const createNote = async () => {
        isUpdatingNote = true;

        selectedNote = {
            ...selectedNote,
            relTo: NoteTypes.PatientEncounter,
            relId: encounter.id,
        };

        await apiMethods.postMethod.createNote(selectedNote).then((res) => {
            if (!res || res.error)
                showError(
                    res.error ??
                        "Failed to create note, please refresh the page and try again and contact support if the issue persists.",
                );
            else hasSelectedNotesChanged = true;
            showInfo("Note Created");
            _closeModifyNotesModal();
            loadNotes(true);
        });

        isUpdatingNote = false;
    };

    const updateNote = async () => {
        isUpdatingNote = true;

        await apiMethods.putMethod.updateNote(selectedNote).then((res) => {
            if (!res || res.error)
                showError(
                    res.error ??
                        "Failed to update note, please refresh the page and try again and contact support if the issue persists.",
                );
            else {
                hasSelectedNotesChanged = true;
                showInfo("Note Updated");
                _closeModifyNotesModal();
                loadNotes(true);
            }
        });

        isUpdatingNote = false;
    };

    const deleteNote = async () => {
        isUpdatingNote = true;

        await apiMethods.deleteMethod
            .deleteNote(selectedNote.id)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to delete note, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    hasSelectedNotesChanged = true;
                    showInfo("Note Deleted");
                    _closeDeleteNoteModal();
                    loadNotes(true);
                }
            });

        isUpdatingNote = false;
    };

    // HELPER FUNCTIONS
    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-center gap-x-2">
                    <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit note">
                        <i class="editIcon ${editIcon}"></i>
                    </div>
                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete note">
                        <i class="deleteIcon ${deleteIcon}"></i>
                    </div>
                </div>`;
    };

    const _openCreateNoteModal = () => {
        selectedNote = { visibleToDoctors: true };
        isCreateNoteModalOpen = true;
        setTimeout(() => {
            createNoteContentInputRef?.focus();
        }, 200);
    };

    const _openEditNoteModal = (note) => {
        selectedNote = { ...note };
        isEditNoteModalOpen = true;
        setTimeout(() => {
            editNoteContentInputRef?.focus();
        }, 200);
    };

    const _closeModifyNotesModal = () => {
        selectedNote = {};
        isCreateNoteModalOpen = false;
        isEditNoteModalOpen = false;
    };

    const _openDeleteNoteModal = (note) => {
        selectedNote = { ...note };
        isDeleteNoteModalOpen = true;
    };

    const _closeDeleteNoteModal = () => {
        selectedNote = {};
        isDeleteNoteModalOpen = false;
    };
</script>

<NotificationModal
    {isModalOpen}
    title={modalTitle}
    modalMaxWidth="max-w-6xl"
    cancelText="Close"
    showConfirmBtn={false}
    closeModal={() => closeModal(hasSelectedNotesChanged)}
    {headerOptions}
    closeModalFunction={() => closeModal(hasSelectedNotesChanged)}
>
    <div class="flex flex-col overflow-y-auto relative h-96">
        {#if isLoadingNotes}
            <PageLoading />
        {:else if !allNotes.length}
            <p>No related notes under selected encounter</p>
        {:else}
            <SvelteTable
                {columns}
                rows={allNotes}
                clickCellFunc={({ detail: { event, row, key } }) => {
                    handleClickCell(event, row, key);
                }}
            />
        {/if}
    </div>
</NotificationModal>

<NotificationModal
    title={`${selectedNote.hasOwnProperty("id") ? "Edit" : "Add"} Note`}
    isModalOpen={isCreateNoteModalOpen || isEditNoteModalOpen}
    isConfirming={isUpdatingNote}
    cancelText="Discard"
    submitText={isUpdatingNote
        ? "Saving..."
        : selectedNote.hasOwnProperty("id")
          ? "Update"
          : "Create"}
    closeModal={_closeModifyNotesModal}
    closeModalFunction={_closeModifyNotesModal}
    confirmAction={selectedNote.hasOwnProperty("id")
        ? () => updateNote()
        : () => createNote()}
    modalMaxWidth="max-w-6xl"
>
    <div class="flex flex-col gap-y-4 flex-1">
        <div class="flex flex-1 w-full gap-x-2">
            <p class="title">Content:</p>
            <!-- <textarea
                class="textarea textarea-bordered w-full flex-1"
                bind:value={selectedNote.content}
                rows="6"
                autocomplete="off"
                bind:this={editNoteContentInputRef}
            /> -->
            <div class="content">
                <RichTextEditor bind:content={selectedNote.content} />
            </div>
        </div>

        <div class="flex flex-1 w-full gap-x-2">
            <p class="title">Visible to Doctors:</p>
            <input
                type="checkbox"
                class="toggle toggle-secondary"
                bind:checked={selectedNote.visibleToDoctors}
            />
        </div>
    </div>
</NotificationModal>

<NotificationModal
    title="Delete"
    isModalOpen={isDeleteNoteModalOpen}
    isConfirming={isUpdatingNote}
    cancelText="Delete"
    closeModal={deleteNote}
    submitText="Cancel"
    confirmButtonClass="btn-accent"
    confirmAction={_closeDeleteNoteModal}
    isReverseBtns
    closeModalFunction={_closeDeleteNoteModal}
>
    Delete Note?
</NotificationModal>

<style>
  .title {
    width: 250px;
  }
  .content {
    width: calc(100% - 250px);
  }
</style>
